import { useState, useEffect, useContext, forwardRef } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

import { usePTS } from "../../context/PTSContext";
import { useRakennusData } from "../../context/RakennusDataContext";
import { useReportTemplateContext } from "../report/ReportTemplate/ReportTemplateContext";
import { ReportTemplateContext } from "../report/ReportTemplate/ReportTemplateContext";

    
const Taloustaulu = forwardRef(({ onBackground }, ref) => {
    // 1. Constants
    const {
      years,
      currentYear,
      footerTotals: totalValues,
    } = usePTS();

    // 2. Get context data safely (always call hooks!)
    const { rakennusData } = useRakennusData(); // your local fallback data
    const reportContext = useContext(ReportTemplateContext);
    const mergedRakennusData = reportContext?.mergedRakennusData;

    // 3. Decide which data to use
    const dataToUse =
        mergedRakennusData?.rakennukset_fulls?.length > 0
        ? mergedRakennusData
        : rakennusData || {};

    // 4. Ensure we have an array
    const buildings = Array.isArray(dataToUse.rakennukset_fulls)
        ? dataToUse.rakennukset_fulls
        : [];

    // 5. Filter main buildings
    const mainBuildings = buildings.filter((r) => r.isMainBuilding);

    // console.log("All buildings:", buildings);
    // console.log("Main buildings:", mainBuildings);


    const huoneistoNeliotRaw = mainBuildings.reduce((sum, r) => {
    if (!r.huoneistoala) return sum;

    const cleaned = String(r.huoneistoala)
        .replace(/\s/g, "")
        .replace(",", ".");

    const val = Number(cleaned);

    return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);

    // ✅ apply fallback AFTER summing
    const huoneistoNeliot = huoneistoNeliotRaw || 1;

    // console.log("Total huoneistoala:", huoneistoNeliot);




    // 2. State
    const [interestRate, setInterestRate] = useState(0.04);      // default 4 %
    const [paymentsPerYear, setPaymentsPerYear] = useState(12);  // default monthly
    const [intervalText, setIntervalText] = useState("");
    const [perM2Values, setPerM2Values] = useState([]);

    // 3. Effects for derived state
    useEffect(() => {
        // human readable interval text
        let text;
        switch (paymentsPerYear) {
            case 12: text = "kuukausittain"; break;
            case 4:  text = "neljännesvuosittain"; break;
            case 2:  text = "puolivuosittain"; break;
            case 1:  text = "vuosittain"; break;
            default: text = `${paymentsPerYear} kertaa vuodessa`;
        }
        setIntervalText(text);
    }, [paymentsPerYear]);

    useEffect(() => {
        // calculate €/m² per year
        if (huoneistoNeliot > 0 && totalValues?.length) {
            const values = totalValues.map(sum => (sum * 1000) / huoneistoNeliot);
            setPerM2Values(values);
        }
    }, [totalValues, huoneistoNeliot]);

    // 4. Helpers
    const formatVal2 = (val) => (val != null ? val.toFixed(2) : "-");
    const formatVal1 = (val) => (val != null ? val.toFixed(1) : "-");
    const formatVal0 = (val) => (val != null ? val.toFixed(0) : "-");

    const calculateAnnuityFactor = (interestRate, loanYears, paymentsPerYear = 12) => {
        const periodRate = interestRate / paymentsPerYear;
        const periods = loanYears * paymentsPerYear;
        return (periodRate * Math.pow(1 + periodRate, periods)) /
               (Math.pow(1 + periodRate, periods) - 1);
    };

    const calculateCumulative = (perM2Values, yearlyMultiplier) => {
        const cumulative = [];
        let runningSum = 0;
        perM2Values.forEach(cost => {
            if (cost != null) {
                runningSum += cost * yearlyMultiplier;
                cumulative.push(runningSum);
            } else {
                cumulative.push(null);
            }
        });
        return cumulative;
    };

    // 5. Main calculations
    const factor10 = calculateAnnuityFactor(interestRate, 10, paymentsPerYear);
    const factor25 = calculateAnnuityFactor(interestRate, 25, paymentsPerYear);

    const cumulative10 = calculateCumulative(perM2Values, factor10);
    const cumulative25 = calculateCumulative(perM2Values, factor25);

    const chartData = years.map((year, idx) => ({
        year,
        "10v laina": cumulative10[idx] || 0,
        "25v laina": cumulative25[idx] || 0,
    }));

    // 6. Debugging/logging
    // useEffect(() => {
    //     console.log("Total values:", totalValues);
    //     console.log("Per m² values:", perM2Values);
    // }, [totalValues, perM2Values]);

    const toMonthly = (val, paymentsPerYear) => {
        if (val == null) return null;
        if (paymentsPerYear === 12) return val; // jo kuukausittain
        return val / (12 / paymentsPerYear);
    };
    // console.log("perM2Values:", perM2Values);
    // console.log("factor10, factor25:", factor10, factor25);
    // console.log("cumulative10:", cumulative10);
    // console.log("cumulative25:", cumulative25);
    // console.log("chartData:", chartData);
    
    const cumulativeMax = Math.max(
    ...cumulative10.filter(v => v != null),
    ...cumulative25.filter(v => v != null)
    );

    return (
        <div ref={ref}>
            <div className="mt-5 mb-3 ptstaulut">
                <div className="mb-5" style={{ width: "100%", height: 300 }}>
                    {/* Small grey chart title */}
                    <div style={{ 
                    textAlign: "center", 
                    fontSize: "0.9rem", 
                    color: "grey", 
                    marginBottom: "0.5rem" 
                    }}>
                    TEOREETTINEN RAHOITUSVASTIKE
                    </div>

                    <ResponsiveContainer>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" />
                            <YAxis
                            tick={{ fontSize: 14, fontFamily: 'Lato', fontWeight: 'bold' }}
                            domain={[0, cumulativeMax * 1.2]} // 20% padding on top
                            tickFormatter={(value) => value.toFixed(2)}
                            />
                            <Tooltip 
                            formatter={(value, name) => [`${value.toFixed(2)} €/m²`, name]} 
                            />
                            <Legend 
                            layout="vertical"       // stack items vertically
                            verticalAlign="bottom"  // position legend below the chart
                            align="center"          // center horizontally
                            />
                            <Line 
                            type="monotone" 
                            dataKey="10v laina" 
                            stroke="#2F5930" 
                            strokeWidth={2} 
                            name={`Rahoitusvastike / huoneistoneliö 10v lainalla ja ${(interestRate*100).toFixed(2)}% korolla`} 
                            />
                            <Line 
                            type="monotone" 
                            dataKey="25v laina" 
                            stroke="#82ca9d" 
                            strokeWidth={2} 
                            name={`Rahoitusvastike / huoneistoneliö 25v lainalla ja ${(interestRate*100).toFixed(2)}% korolla`} 
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>



                {/* 🔹 Your original table, untouched */}
                <div className="table-responsive">
                    <table className="table table-sm table-borderless table-striped mb-0">
                        {/* Header */}
                        <thead>
                            <tr>
                                <th colSpan={years.length + 2} className="bg-success text-white p-2">
                                    <div className="d-flex justify-content-between">
                                        <div className="fw-bold">Kustannusten jakautuminen</div>
                                        <div className="small text-end">Kustannustaso {currentYear} sis. Alv 25,5%</div>
                                    </div>
                                </th>
                            </tr>
                        </thead>

                        {/* Column headers */}
                        <thead>
                            <tr>
                                <th className="bg-success text-white text-start"></th>
                                {years.map(year => (
                                    <th key={year} className="bg-success text-white text-center px-2 fw-bold">
                                        {year}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {/* Total costs */}
                            <tr className="bg-success text-white">
                                <td className="text-start">
                                    Kustannukset taloyhtiölle yhteensä vuosittain <br /> 
                                    <div className="text-center">(x 1000€)</div>
                                </td>
                                {totalValues.map((sum, idx) => (
                                    <td key={idx} className="text-center font-monospace align-middle">{sum != null ? sum : "-"}</td>
                                ))}
                            </tr>

                            <tr><td colSpan={years.length + 1}>&nbsp;</td></tr>

                            {/* €/m² row */}
                            <tr className="bg-success text-white">
                                <td className="text-start">Kustannukset vuosittain € / huoneistoneliö</td>
                                {perM2Values.map((val, idx) => (
                                    <td key={idx} className="text-center font-monospace px-2">{formatVal2(val)}</td>
                                ))}
                            </tr>
    {/* 
                            <tr><td colSpan={years.length + 1}>&nbsp;</td></tr> */}

            
                            {/* <tr className="bg-success text-white">
                                <td className="text-start">
                                    Rahoitusvastike per huoneistoneliö 10v, {(interestRate*100).toFixed(2)}% korko
                                </td>
                                {cumulative10.map((val, idx) => (
                                    <td key={idx} className="text-center font-monospace px-2">{formatVal2(val)}</td>
                                ))}
                            </tr>

                            <tr className="bg-success text-white">
                                <td className="text-start">
                                    Rahoitusvastike per huoneistoneliö 25v, {(interestRate*100).toFixed(2)}% korko
                                </td>
                                {cumulative25.map((val, idx) => (
                                    <td key={idx} className="text-center font-monospace px-2">{formatVal2(val)}</td>
                                ))}
                            </tr> */}

                            <tr><td colSpan={years.length + 1}>&nbsp;</td></tr>

                            {/* Total over 10 years */}
                            <tr>
                                <td className="text-start">Kustannukset yhteensä 10 vuoden jaksolla</td>
                                <td colSpan={years.length + 1} className="font-monospace px-2">
                                    {(totalValues.reduce((acc, val) => acc + (val || 0), 0) * 1000).toLocaleString('fi-FI')} €
                                </td>
                            </tr>

                            <tr><td colSpan={years.length + 1}>&nbsp;</td></tr>

                            {/* Huoneistoala */}
                            <tr>
                                <td className="text-start">Kohteen huoneistoala</td>
                                <td className="ps-5" colSpan={years.length + 1}>{huoneistoNeliot} m²</td>
                            </tr>

                            <tr>
                                <td className="text-start">Kertakustannus per huoneistoneliö 10v ajanjaksolla</td>
                                <td className="ps-5" colSpan={years.length + 1}>
                                    {formatVal0((totalValues.reduce((acc, val) => acc + (val || 0), 0) * 1000) / huoneistoNeliot).toLocaleString('fi-FI')} € /m²
                                </td>
                            </tr>
                        </tbody>

                        {/* Footer */}
                        <tfoot>
                        {[{years: cumulative10, label: "10v"}, {years: cumulative25, label: "25v"}].map((item, i) => {
                            const val = item.years[item.years.length - 1];
                            const year = years[item.years.length - 1];
                            return (
                            <tr key={i} className="fw-bold">
                                <td colSpan={years.length + 2} className="bg-success text-white p-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>
                                    Teoreettinen rahoitusvastike {item.label} lainalla ja {(interestRate*100).toFixed(2)}% korolla {intervalText}
                                    </span>
                                    <span>
                                    {formatVal1(val)} €/m²{year ? ` Vuonna ${year}` : ""}
                                    </span>
                                </div>
                                </td>
                            </tr>
                            );
                        })}
                        </tfoot>
                    </table>
                </div>
            </div>
            
            {!onBackground && (
                <div className="mb-5 d-flex flex-column flex-sm-row justify-content-center gap-3 align-items-center">
                    <div className="text-center text-sm-start">
                        <label className="fw-bold d-block text-center">Korko (%):</label>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={(interestRate * 100).toFixed(2)}
                                onChange={e => setInterestRate(parseFloat(e.target.value) / 100)}
                                className="form-control w-auto mx-auto text-center"
                        />
                    </div>
                    {/* <div className="text-center text-sm-start">
                        <label className="fw-bold">Maksutiheys:</label>
                        <select
                            value={paymentsPerYear}
                            onChange={e => setPaymentsPerYear(parseInt(e.target.value))}
                            className="form-select w-auto mx-auto"
                        >
                            <option value={12}>Kuukausittain</option>
                            <option value={4}>Neljännesvuosittain</option>
                            <option value={2}>Puolivuosittain</option>
                            <option value={1}>Vuosittain</option>
                        </select>
                    </div> */}
                </div>

            )}
            
            </div>
    );
});

export default Taloustaulu;
